import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Grid, TablePagination, Typography, useMediaQuery} from "@mui/material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import {
  validateEmail, validateName,
  validatePhoneNumber
} from "../../../util/ValidationUtils";
import {createNewUser, updateExistingUser, deleteUser, getAccountUsers} from "../request/user-requests";
import Busy from "../../Busy";
import UsersTable from "./UsersTable";
import UsersCards from "./UsersCards";
import UsersToolbar from "./UsersToolbar";
import {DEFAULT_USER_SORT_VALUE, getRoleFilterOptions, USER_SORT_OPTIONS} from "./userDisplayUtils";
import { SubscriptionType, UserType, UserTypeDBFormat} from "../../constants/securspace-constants";
import CreateEditUser from "./CreateEditUser";
import {withSnackbar} from "../../hocs/withSnackbar";
import {getErrorMessageForNonStandardAndStandardResponse, isSmsVerificationFailure} from '../../../util/NetworkErrorUtil';
import UserDialog from "./UserDialog";
import { isBuyerAccount, isSupplierAccount } from "../../../util/AccountUtils";
import EmailVerifyGuardTooltip from "../../notifications/EmailVerifyGuardTooltip";

const ALL_ROLES_FILTER = 'All roles';

const NEW_USER = Object.freeze({
  username: "",
  role: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  locationIds: [],
  isLocationManager: false,
  isOptedIntoText: false,
});

// Stable serialization of the editable form fields, used to detect unsaved (dirty) changes
// when the drawer is closed before saving.
const serializeUserForm = (user) => JSON.stringify({
  username: user.username || '',
  firstName: user.firstName || '',
  lastName: user.lastName || '',
  phoneNumber: user.phoneNumber || '',
  role: user.role || '',
  isOptedIntoText: !!user.isOptedIntoText,
  locationIds: [...(user.locationIds || [])].sort(),
});

const UserManagement = ({account, snackbarShowMessage}) => {
  const [users, setUsers] = useState([]);
  const [userToEdit, setUserToEdit] = useState({...NEW_USER});
  const [userToDelete, setUserToDelete] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [showCreateEditUser, setShowCreateEditUser] = useState(false);
  const [emailErrorMsg, setEmailErrorMsg] = useState('Please enter an email address.');
  const [firstNameErrorMsg, setFirstNameErrorMsg] = useState("Please enter the user's first name.");
  const [lastNameErrorMsg, setLastNameErrorMsg] = useState("Please enter the user's last name");
  const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState(''); // not required
  const [roleErrorMsg, setRoleErrorMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [showEditAlert, setShowEditAlert] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogText, setDialogText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState(ALL_ROLES_FILTER);
  const [sortValue, setSortValue] = useState(DEFAULT_USER_SORT_VALUE);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const initialFormSnapshotRef = useRef(serializeUserForm(NEW_USER));

  const isBuyer = isBuyerAccount(account);
  // Switch to the card layout below lg — at narrow-desktop widths the 6-column table
  // squeezes the username/email column enough to wrap it character-by-character.
  const isCompactView = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const roleFilterOptions = useMemo(() => getRoleFilterOptions(isBuyer), [isBuyer]);
  const hasActiveFilters = !!searchTerm.trim() || roleFilter !== ALL_ROLES_FILTER;

  const loadAccountUsers = useCallback(() => {
    const sortOption = USER_SORT_OPTIONS.find(option => option.value === sortValue) || USER_SORT_OPTIONS[0];
    const roleOption = roleFilterOptions.find(option => option.value === roleFilter);
    const roles = roleOption ? roleOption.roles : [];

    Busy.set(true);
    getAccountUsers(account.id, {
      page,
      size: rowsPerPage,
      searchTerm,
      roles,
      sortBy: sortOption.sortBy,
      sortDir: sortOption.sortDir,
    }).then(response => {
      Busy.set(false);
      const {body} = response;
      if (body) {
        const usersList = body.content || [];
        setUsers(usersList);
        setTotalCount(body.count || 0);
        const usersWithAdminOrOwner = usersList.filter(user => (user.role === UserTypeDBFormat.ROLE_USERTYPE_ADMIN || user.role === UserTypeDBFormat.ROLE_USERTYPE_OWNER))
        setAdminUsers(usersWithAdminOrOwner)
      }
    }).catch(() => {
      Busy.set(false);
      snackbarShowMessage('Failed to fetch users. Please contact support.', 'error', 15000);
    });
  }, [account, snackbarShowMessage, page, rowsPerPage, searchTerm, roleFilter, sortValue, roleFilterOptions]);

  useEffect(() => {
    loadAccountUsers();
  }, [loadAccountUsers]);

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search is debounced by DelayedTextField; reset to the first page whenever the filter changes.
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setPage(0);
  }, []);

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
    setPage(0);
  };

  const handleSortChange = (value) => {
    setSortValue(value);
    setPage(0);
  };

  const handleAddUserClick = () => {
    setIsEdit(false);
    setUserToEdit({...NEW_USER});
    initialFormSnapshotRef.current = serializeUserForm(NEW_USER);
    setShowCreateEditUser(true);
  };

  // Actually closes the drawer and resets the working copy. Bypasses the dirty check —
  // used after a successful save or a confirmed discard.
  const doCloseCreateEditUser = () => {
    setShowDiscardConfirm(false);
    setShowCreateEditUser(false);
    setUserToEdit({...NEW_USER});
  };

  // Close requested via the drawer (X, backdrop, Esc, or Cancel). Guard unsaved changes.
  const handleRequestCloseCreateEditUser = () => {
    const isDirty = serializeUserForm(userToEdit) !== initialFormSnapshotRef.current;
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      doCloseCreateEditUser();
    }
  };

  const emailValidation = (value) => {
    if (!value || !value.trim()) return 'Please enter an email address.';
    return validateEmail(value) ? '' : 'Email address is invalid.';
  };

  const nameValidation = (value, label) => {
    if (!value || !value.trim()) {
      return `Please enter the user's ${label}.`;
    }
    return validateName(value) ? '' : `${label} can contain only spaces, hyphens, and alphanumeric characters`;
  };

  const handleSimpleTextChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    switch (name) {
      case 'email':
        setUserToEdit({...userToEdit, username: value});
        setEmailErrorMsg(emailValidation(value));
        break;
      case 'firstName':
        setUserToEdit({...userToEdit, firstName: value});
        setFirstNameErrorMsg(nameValidation(value, 'First name'));
        break;
      case 'lastName':
        setUserToEdit({...userToEdit, lastName: value})
        setLastNameErrorMsg(nameValidation(value, 'Last name'));
        break;
      default:
        break;
    }
  };

  const handlePhoneNumberChange = (value) => {
    // PhoneInput returns undefined for empty string so convert to ''
    setUserToEdit({...userToEdit, phoneNumber: value || ''});
  };

  const handleLocationIdsChange = (value) => {
    setUserToEdit({...userToEdit, locationIds: value});
  }

  const handleSmsOptInChange = (event) => {
    setUserToEdit({...userToEdit, isOptedIntoText: event.target.checked});
  };

  const handleSelectChange = (event) => {
    setUserToEdit({
      ...userToEdit,
      role: event.target.value,
    });
    setRoleErrorMsg('');
  };

  const handleEditUser = (user) => {
    setIsEdit(true);

    setShowCreateEditUser(true);
    setUserToEdit({...user});
    initialFormSnapshotRef.current = serializeUserForm(user);
    setEmailErrorMsg('');
    setFirstNameErrorMsg('');
    setLastNameErrorMsg('');
    setPhoneNumberErrorMsg('');
    setRoleErrorMsg('');
  };

  const submitDeleteUserToApi = () => {
    setShowDeleteAlert(false);
    if (userToDelete.username === account.username) {
      snackbarShowMessage('Account Owner cannot be deleted.', 'error', 15000);
      return;
    }

    Busy.set(true);
    deleteUser(userToDelete).then(() => {
      Busy.set(false);
      loadAccountUsers();
    }).catch(() => {
      Busy.set(false);
      snackbarShowMessage('Failed to delete user. Please contact support.', 'error', 15000);
    });
  }

  const handleDeleteUser = (user) => {
    if (user.isLocationManager) {
      setDialogTitle('Delete Location Manager?');
      setDialogText('Removing this user will also remove a Location Manager. You are able to select a new Location Manager at the Location Management page.');
      setShowDeleteAlert(true);
    } else {
      setDialogTitle('Delete User?');
      setDialogText('This user will no longer be able to access this account. Are you sure you want to delete this user?');
      setShowDeleteAlert(true);
    }
  };

  // Menu/card actions: capture the target user, then open the confirmation dialog.
  const handleDeleteUserRequest = (user) => {
    setUserToDelete(user);
    handleDeleteUser(user);
  };

  const submitToApi = (event) => {
    event.preventDefault();
    setShowEditAlert(false);
    // These fields validate on change
    if (emailErrorMsg ||
      firstNameErrorMsg ||
      lastNameErrorMsg
    ) {
      if (!userToEdit.role) {
        setRoleErrorMsg('Please select a role.');
      }
      setFormErrorMsg('Please complete the form to proceed');
      return;
    } else {
      // These fields validate on submit
      if (userToEdit.phoneNumber && !validatePhoneNumber(userToEdit.phoneNumber, true)) {
        setPhoneNumberErrorMsg('Phone number must be 10 digits.');
        setFormErrorMsg('Please complete the form to proceed');
        return;
      } else {
        setPhoneNumberErrorMsg('');
      }
      setFormErrorMsg('');
    }

    let locationIds = userToEdit.locationIds;
    if (locationIds?.includes('all')) {
      locationIds = isEdit ? null : [];
    } else if (Array.isArray(locationIds)) {
      // Guard against null/undefined entries reaching the API, which would
      // violate the backend's non-null location_id constraint.
      locationIds = locationIds.filter(id => typeof id === 'string' && id.length > 0);
    }

    // NOTE: backend expects null for phoneNumber if empty string
    const body = {
      accountId: account.id,
      username: userToEdit.username,
      firstName: userToEdit.firstName.trim(),
      lastName: userToEdit.lastName.trim(),
      phoneNumber: userToEdit.phoneNumber ? userToEdit.phoneNumber : null,
      role: userToEdit.role,
      locationIds: locationIds,
      isLocationManager: userToEdit?.isLocationManager,
      isOptedIntoText: !!userToEdit?.isOptedIntoText,
    };

    Busy.set(true)
    // A failed SMS verification is a partial success: the API commits the user before responding,
    // so the save itself worked and only text notifications could not be enabled. Finish the
    // success path (close the panel, refresh the list so the SMS badge updates) and warn, rather
    // than leaving the form open as though the edit was rejected.
    const finishSave = (successMessage) => (error) => {
      Busy.set(false);
      setShowCreateEditUser(false);
      loadAccountUsers();
      setUserToEdit({...NEW_USER});
      if (error) {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'warning', 15000);
      } else {
        snackbarShowMessage(successMessage, 'success');
      }
    };

    if (isEdit) {
      const onEditSettled = finishSave("User information has been successfully updated.");
      updateExistingUser(body).then(() => onEditSettled()).catch(error => {
        if (isSmsVerificationFailure(error)) {
          onEditSettled(error);
          return;
        }
        Busy.set(false);
        const errorMessage = getErrorMessageForNonStandardAndStandardResponse(error);
        snackbarShowMessage(`Failure to update user information. ${errorMessage}`, 'error', 15000);
      });
    } else {
      const onCreateSettled = finishSave("User successfully added. User will receive an email to create a password.");
      createNewUser(body).then(() => onCreateSettled()).catch(error => {
        if (isSmsVerificationFailure(error)) {
          onCreateSettled(error);
          return;
        }
        Busy.set(false);
        const errorMessage = getErrorMessageForNonStandardAndStandardResponse(error)
        snackbarShowMessage(errorMessage, 'error', 15000);
      });
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSupplierAccount(account)) {
      if (userToEdit?.isLocationManager && userToEdit?.role === UserTypeDBFormat.ROLE_USERTYPE_GATECLERK) {
        setDialogTitle('Downgrade Usertype?');
        setDialogText('Changing this user to a Gate Clerk will also remove a Location Manager. You are able to select a new Location Manager at the Location Management page.');
        setShowEditAlert(true);
      } else {
        submitToApi(event);
      }
    } else {
      submitToApi(event);
    }
  };

  const roleBasedOptions = useMemo(() => {
    let roles = [];

    if (isSupplierAccount(account)) {
      if (account.subscriptionType === SubscriptionType.MARKETPLACE_ONLY) {
        roles.push(UserTypeDBFormat.ROLE_USERTYPE_OWNER);
      } else {
        roles.push(UserTypeDBFormat.ROLE_USERTYPE_OWNER, UserTypeDBFormat.ROLE_USERTYPE_GATECLERK, UserTypeDBFormat.ROLE_USERTYPE_GATEMANAGER);
      }
    } else if (isBuyerAccount(account)) {
      roles.push(UserTypeDBFormat.ROLE_USERTYPE_OWNER, UserTypeDBFormat.ROLE_USERTYPE_DISPATCHER);
    }
    
    return roles;
  }, [account]);

  const canEditRoleBasedOptions = useMemo(() => {
    let options = roleBasedOptions;
    if ([UserType.ADMIN, UserType.SUPER_ADMIN, UserType.PRIMARY_OWNER].includes(account.userType)) {
      options = [...options, UserTypeDBFormat.ROLE_USERTYPE_PRIMARY_OWNER]
    }
    return options;
    
  }, [roleBasedOptions, account])

  return (
    <>
      <UserDialog
        showAlert={showEditAlert}
        setShowAlert={setShowEditAlert}
        dialogTitle={dialogTitle}
        dialogText={dialogText}
        submitToApi={submitToApi}
      />
      <UserDialog
        showAlert={showDeleteAlert}
        setShowAlert={setShowDeleteAlert}
        dialogTitle={dialogTitle}
        dialogText={dialogText}
        submitToApi={submitDeleteUserToApi}
      />
      <UserDialog
        showAlert={showDiscardConfirm}
        setShowAlert={setShowDiscardConfirm}
        dialogTitle={'Discard changes?'}
        dialogText={'You have unsaved changes. If you close now, your changes will be lost.'}
        submitToApi={doCloseCreateEditUser}
      />
      <Grid component={'article'} container>
        <Grid item container justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant={'h5'} component={'h1'} color={'textPrimary'}>Users</Typography>
          <EmailVerifyGuardTooltip
            disabled={!account?.emailVerified}
            disabledReason={"Please verify your email address to add users."}
          >
            <Button variant={'contained'} color={'primary'} onClick={handleAddUserClick} disabled={!account?.emailVerified}>Add User</Button>
          </EmailVerifyGuardTooltip>
        </Grid>
        <CreateEditUser
          open={showCreateEditUser}
          onClose={handleRequestCloseCreateEditUser}
          onSubmit={handleSubmit}
          onTextChange={handleSimpleTextChange}
          onSelectChange={handleSelectChange}
          onPhoneNumberChange={handlePhoneNumberChange}
          handleLocationIdsChange={handleLocationIdsChange}
          onSmsOptInChange={handleSmsOptInChange}
          isEdit={isEdit}
          isBuyer={isBuyerAccount(account)}
          user={userToEdit}
          emailErrorMsg={emailErrorMsg}
          firstNameErrorMsg={firstNameErrorMsg}
          lastNameErrorMsg={lastNameErrorMsg}
          phoneNumberErrorMsg={phoneNumberErrorMsg}
          roleErrorMsg={roleErrorMsg}
          roleBasedOptions={roleBasedOptions}
          formErrorMsg={formErrorMsg}
          adminUsersArray={adminUsers}
          account={account}
        />
        <Grid item xs={12}>
          <UsersToolbar
            search={searchTerm}
            onSearchChange={handleSearchChange}
            roleFilter={roleFilter}
            roleFilterOptions={roleFilterOptions}
            onRoleFilterChange={handleRoleFilterChange}
            sortValue={sortValue}
            onSortChange={handleSortChange}
          />
          {users.length === 0 ? (
            <Box
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 6,
                textAlign: 'center',
              }}
            >
              <PersonOffIcon sx={{ fontSize: 28, color: 'action.disabled', mb: 1 }} />
              <Typography variant={'body2'} color={'text.secondary'}>
                {hasActiveFilters
                  ? 'No users match your search.'
                  : 'There are currently no users for this account.'}
              </Typography>
            </Box>
          ) : isCompactView ? (
            <UsersCards
              users={users}
              isBuyer={isBuyer}
              roleBasedOptions={canEditRoleBasedOptions}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteUserRequest}
            />
          ) : (
            <UsersTable
              users={users}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteUserRequest}
              roleBasedOptions={canEditRoleBasedOptions}
              isBuyer={isBuyer}
            />
          )}
          <TablePagination
            component={'div'}
            count={totalCount}
            page={page}
            rowsPerPageOptions={[5, 10, 25]}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Grid>
      </Grid>
    </>
  );
};

UserManagement.propTypes = {
  account: PropTypes.object.isRequired,
};

export default withSnackbar(UserManagement);
